/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   syncMapChange thunk
 */

import { updateUrlFromLocationFilter } from '@zcode-ui/filters'
import { selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { ZOOM_HOOD, isCommonAuthError } from '@zcode/nr-utils'
import type { BoundsObj, State } from '@zcode/state-type'
import type { Empty, MaybeEmpty } from '@zcode/utility-types'
import { getCurrentCityPath } from '../../../inspectors'
import { logger } from '../../../logger'
import locateCenter from '../../helpers/locateCenter'
import {
  boxChanged,
  centerChanged,
  cityNameChanged,
  clearCityName,
  clearHoodName,
  clearStateName,
  hoodNameChanged,
  stateNameChanged,
  urlChanged,
  zoomChanged,
} from '../../location/actions'
import {
  selectCityName,
  selectCountry,
  selectHoodName,
  selectStateName,
  selectUrl,
  selectZoom,
} from '../../location/selectors'
import { mixpanelTrackZoom } from '../../mixpanel/thunks'
import type { Dispatch, GetState } from '../../types'
import { selectMapBox, selectMapCenter, selectMapReady, selectMapZoom } from '../selectors'
import { isBoxChanged, isCenterChanged, zoomChangedToHoodZoom } from './shouldSyncMapChange'

function isEmpty(box: MaybeEmpty<BoundsObj> | null): box is Empty<BoundsObj> | null {
  return (
    box == null ||
    (box.maxLat === undefined &&
      box.maxLng === undefined &&
      box.minLat === undefined &&
      box.minLng === undefined)
  )
}

export const syncMapChange = () => async (dispatch: Dispatch, getState: GetState) => {
  const state: State = getState()

  const mapBox = selectMapBox(state)
  const mapCenter = selectMapCenter(state)
  const mapZoom = selectMapZoom(state)
  const isMapReady = selectMapReady(state)
  const isHoodZoom = mapZoom && mapZoom >= ZOOM_HOOD
  const currentZoom = selectZoom(state)
  const cityState = getCurrentCityPath(state)
  const isMobile = selectIsMobile(state)

  const boxUpdated = isBoxChanged(state)
  const centerUpdated = isCenterChanged(state)
  const zoomUpdated = zoomChangedToHoodZoom(state)

  // let's update the location's box, center, and zoom

  dispatch(boxChanged(isEmpty(mapBox) ? null : mapBox))
  dispatch(zoomChanged(mapZoom))
  dispatch(centerChanged(mapCenter))

  if (mapZoom !== currentZoom) {
    dispatch(mixpanelTrackZoom({ cityState, isMobile, zoom: Math.round(mapZoom) }))
  }

  let promise = Promise.resolve(false)
  if (centerUpdated || zoomUpdated) {
    promise = locateCenter(mapCenter, mapBox)
      .then(({ city, hood }) => {
        const currentCityName = selectCityName(state)
        const currentHoodName = selectHoodName(state)
        const currentStateName = selectStateName(state)
        const currentCountryName = selectCountry(state)
        const currentUrl = selectUrl(state)

        const cityName = city ? city.name : undefined
        const hoodName = hood ? hood.name : undefined
        const stateName = city ? city.state : undefined
        const cityUrl = city ? city.path : undefined
        const hoodUrl = hood ? hood.path : undefined
        const url = isHoodZoom && hoodUrl ? hoodUrl : cityUrl

        // reconcile the city labels and url in the current location...
        const shouldUpdateCityName = cityName !== currentCityName
        const shouldUpdateHoodName = hoodName !== currentHoodName
        const shouldUpdateStateName = stateName !== currentStateName
        const shouldUpdateUrl = currentUrl !== url

        if (shouldUpdateCityName && cityName) {
          dispatch(cityNameChanged(cityName))
        } else if (shouldUpdateCityName) {
          dispatch(clearCityName())
        }
        if (isHoodZoom && shouldUpdateHoodName && hoodName) {
          dispatch(hoodNameChanged(hoodName))
        } else if ((!isHoodZoom || shouldUpdateHoodName) && currentHoodName) {
          dispatch(clearHoodName())
        }
        if (shouldUpdateStateName && stateName) {
          dispatch(stateNameChanged(stateName))
        } else if (shouldUpdateStateName) {
          dispatch(clearStateName())
        }
        if (shouldUpdateUrl) {
          dispatch(urlChanged(url ?? null))
        }
        return shouldUpdateUrl
      })
      .catch((e: unknown) => {
        if (!isCommonAuthError(e)) {
          logger.error(
            'Failed to locate map center',
            typeof e === 'object' && e && 'data' in e ? e.data || e : e
          )
        }
        return false
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  promise.then((shouldUpdateUrl) => {
    // NOTE: we do not want to update the URL during cold load. Normally we update the box in
    // the URL. Here we skip updating the URL when the map is not done (implying that this is
    // the first map load after a cold load). Some warm loads (link homepage to map list) will also
    // appear as cold loads by this measure.
    //
    // TODO: when warm loading a url we will inadvertantly add the box if it doesn't match the map.
    // homepage -> chicago (should not add box)
    //
    // Update the router url
    if ((boxUpdated || shouldUpdateUrl) && isMapReady) {
      const shouldOnlyAddBox = boxUpdated && !shouldUpdateUrl
      const shouldOnlyAddHood = !shouldOnlyAddBox && zoomUpdated
      const shouldReplace = shouldOnlyAddBox || shouldOnlyAddHood
      dispatch(
        updateUrlFromLocationFilter({
          shouldReplace,
          omitBox: undefined,
          keepSameBox: undefined,
        })
      )
    }
  })
  return await promise
}
